import React, { useMemo } from "react";

import { css, cx } from "@emotion/css";
import { Group } from "@visx/group";
import { Circle, Line } from "@visx/shape";
import { DateTime } from "luxon";

import { Icons } from "@/assets/icons.ts";
import { useMousePosition } from "@/hooks/useMousePosition.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { DayData } from "@/models/CGMDailyGraphData.ts";
import { DiscreteBasalInsulin } from "@/models/diabetes/InsulinModel.ts";
import { InsulinBasalRate } from "@/models/InsulinBasalRate.ts";
import { PumpEvent } from "@/models/InsulinPumpEvents.ts";
import { useTheme } from "@/theme/CreateTheme.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { BADGE_WIDTH, SvgBadge } from "@/uiKit/atoms/svg/SvgBadge.tsx";
import { SvgDot } from "@/uiKit/atoms/svg/SvgDot.tsx";
import { SvgText } from "@/uiKit/atoms/svg/SvgText.tsx";
import { GraphAreaListener } from "@/uiKit/molecules/graphs/GraphEvents/GraphAreaListener.tsx";
import {
  BasalRateGraphTooltip,
  InsulinPointTooltip,
  PumpEventTooltip,
} from "@/uiKit/molecules/graphs/InsulinGraph/InsulinTooltips.tsx";
import { toDateTime } from "@/utils/dateUtils.ts";
import {
  SvgCoords,
  SvgLayout,
  SvgPoint,
  boundedDateTimeToSecond,
  dayScale,
  findHoveredLine,
  mergeOverlappingData,
  offsetDataPoint,
  zeroScale,
} from "@/utils/graphUtils.ts";
import { formatNumber } from "@/utils/mathUtils.ts";

export const MAX_BASAL_RATE = 1;
export const PUMP_EVENT_RADIUS = 10;
const BUNCHED_ICONS_OFFSET = 16;

export type ContinuousInsulinGraphProps = {
  date: string;
  discrete: DiscreteBasalInsulin[];
  insulinBasalRates: DayData<InsulinBasalRate>;
  pumpEvents: PumpEvent[];
  className?: string;
  paddingTop?: number;
} & SvgLayout;

type ContinuousInsulinGraphLayout = {
  container: SvgLayout;
  mouseLine: (point: SvgPoint) => SvgCoords;
  graphArea: SvgLayout;
  eventArea: SvgLayout;
};

const pumpEventDisplayName = {
  tubing_filled: "T",
  cannula_filled: "C",
  cartridge_filled: "R",
  pod_changed: "P",
  pod_activated: "A",
  pod_deactivated: "D",
  profile_changed: "⚙️",
} satisfies Record<PumpEvent["category"], string>;

const ContinuousInsulinGraph_: React.FC<ContinuousInsulinGraphProps> = ({
  date,
  insulinBasalRates,
  discrete,
  pumpEvents,
  className,
  paddingTop = 0,
  left,
  top,
  width,
  height,
}) => {
  const styles = useStyles(makeStyles);
  const { onMouseLeave, onMouseMove, position } = useMousePosition();
  const layout = useMemo(
    () => getLayout({ left, top, width, height }, paddingTop),
    [left, top, width, height, paddingTop],
  );
  const ratesList: InsulinBasalRate[] = [];
  if (insulinBasalRates.firstOfNextDay) {
    ratesList.push(insulinBasalRates.firstOfNextDay);
  }
  ratesList.push(...insulinBasalRates.data);
  if (insulinBasalRates.lastOfPreviousDay) {
    ratesList.push(insulinBasalRates.lastOfPreviousDay);
  }

  const theme = useTheme();
  const xScale = dayScale(layout.graphArea.width);
  const maxBasal = Math.max(...ratesList.map(item => item.basalRate), MAX_BASAL_RATE);

  const yScale = zeroScale(layout.graphArea.height, maxBasal);
  const scaledMaxBasal = yScale(maxBasal);

  const day = toDateTime(date);

  // Convert data to x, y coordinates
  const coordData = ratesList.toReversed().map(item => ({
    data: item,
    x: xScale(boundedDateTimeToSecond(day, item.date)),
    y: yScale(item.basalRate),
  }));

  const insulinRateLine: (SvgCoords & { data: InsulinBasalRate })[] = [];
  for (let i = 0; i < coordData.length - 1; i++) {
    const line = {
      from: { x: coordData[i].x, y: coordData[i].y },
      to: { x: coordData[i + 1].x, y: coordData[i].y },
      data: coordData[i].data,
    };
    insulinRateLine.push(line);
  }
  const hoveredLine = findHoveredLine(insulinRateLine, position?.x);

  const mergedPumpEvents = mergeOverlappingData<PumpEvent>(
    pumpEvents.toReversed(),
    ins => xScale(boundedDateTimeToSecond(day, ins.date)),
    BADGE_WIDTH,
  );

  const pumpEventsCoords = mergedPumpEvents.map(merged => ({
    data: merged.data,
    x: merged.x,
    y: 0,
    id: merged.data[0].id,
    count: merged.data.length,
  }));

  const mergedDiscreteData = mergeOverlappingData<DiscreteBasalInsulin>(
    discrete,
    ins => xScale(boundedDateTimeToSecond(day, ins.date)),
    BADGE_WIDTH,
  );

  const coordDiscreteData = mergedDiscreteData.map(merged => ({
    data: merged.data,
    x: merged.x,
    y: merged.data.reduce((acc, ins) => acc + ins.quantity, 0),
    count: merged.data.length,
  }));

  return (
    <Group className={cx(styles.container, className)} {...layout.container}>
      <Group {...layout.graphArea}>
        <SvgText
          variant="captionSmall"
          left={-6}
          top={scaledMaxBasal - 3}
          textAnchor={"end"}
          className={styles.maxBasal}
        >
          {formatNumber(maxBasal, 1)}
        </SvgText>
        <Line x1={0} x2={-4} y1={scaledMaxBasal} y2={scaledMaxBasal} className={styles.maxBasalLine} />
        {insulinRateLine.map(line => (
          <rect
            key={`line-${line.data.id}`}
            className={styles.continuousArea}
            x={line.from.x}
            y={line.from.y}
            width={line.to.x - line.from.x + 1}
            height={layout.graphArea.height - line.to.y}
          />
        ))}
        {insulinRateLine.map((line, index) => (
          <Group key={`line-${line.data.id}`}>
            <Line
              className={cx(styles.continuousLine, {
                [styles.tempLine]: line.data.tempRate,
              })}
              from={line.from}
              to={line.to}
            />
            {index + 1 < insulinRateLine.length ? (
              <Line
                className={styles.continuousLine}
                from={{ x: line.to.x, y: line.to.y }}
                to={{ x: line.to.x, y: insulinRateLine[index + 1].from.y }}
              />
            ) : null}
          </Group>
        ))}

        {hoveredLine && (
          <BasalRateGraphTooltip
            item={hoveredLine.data}
            date={
              position?.x ? DateTime.fromSeconds(xScale.invert(position.x) + toDateTime(day).toSeconds()) : undefined
            }
          >
            <Line
              {...layout.mouseLine({
                x: position?.x ?? 0,
                y: hoveredLine.from.y,
              })}
              className={styles.mouseLine}
            />
            <Circle className={styles.selectedPoint} cx={position?.x ?? 0} cy={hoveredLine.from.y} r={4} />
          </BasalRateGraphTooltip>
        )}
      </Group>
      <GraphAreaListener onMouseMove={onMouseMove} onMouseLeave={onMouseLeave} {...layout.eventArea} />
      {pumpEventsCoords.map(events => {
        return (
          <PumpEventTooltip key={events.id} events={events.data}>
            {events.data.map((event, index) => (
              <SvgDot
                key={event.id}
                top={paddingTop + PUMP_EVENT_RADIUS}
                left={events.x + offsetDataPoint(index, events.count, BUNCHED_ICONS_OFFSET)}
                color={theme.palette.yellow}
                radius={PUMP_EVENT_RADIUS}
                content={pumpEventDisplayName[event.category]}
              />
            ))}
          </PumpEventTooltip>
        );
      })}
      {coordDiscreteData.map(point => (
        <InsulinPointTooltip key={point.x} insulins={point.data} x={BADGE_WIDTH / 2}>
          <SvgBadge
            key={point.x}
            text={`${formatNumber(point.y, 2)} U`}
            Icon={Icons.injection}
            left={point.x}
            top={paddingTop}
            color={theme.palette.insulin.basal}
            dot={point.count > 1 ? point.count : false}
          />
        </InsulinPointTooltip>
      ))}
    </Group>
  );
};

export const ContinuousInsulinGraph = React.memo(ContinuousInsulinGraph_);

const getLayout = (dim: SvgLayout, verticalPadding: number): ContinuousInsulinGraphLayout => {
  return {
    container: dim,
    mouseLine: point => ({
      from: { x: point.x, y: -verticalPadding },
      to: { x: point.x, y: dim.height - verticalPadding },
    }),
    graphArea: {
      top: verticalPadding,
      left: 0,
      width: dim.width,
      height: dim.height - verticalPadding,
    },
    eventArea: {
      top: 0,
      left: 0,
      width: dim.width,
      height: dim.height,
    },
  };
};

const makeStyles = (theme: Theme) => ({
  container: css``,
  maxLine: css({
    stroke: theme.palette.grey[200],
    strokeDasharray: "6, 3",
  }),
  continuousLine: css({
    stroke: theme.palette.insulin.basal.light,
    strokeWidth: 2,
    strokeLinecap: "square",
  }),
  tempLine: css({
    strokeDasharray: "1, 4",
  }),
  continuousArea: css({
    fill: theme.palette.insulin.basal.contrastText,
    stroke: "transparent",
  }),
  continuousInsulinText: css({
    textAnchor: "end",
    stroke: "transparent",
    fill: theme.palette.insulin.basal.main,
  }),
  mouseLine: css({
    fill: theme.palette.grey[300],
    stroke: theme.palette.grey[300],
    strokeWidth: 1,
  }),
  selectedPoint: css({
    color: theme.palette.insulin.basal.light,
    fill: "currentColor",
    stroke: "currentColor",
  }),
  maxBasal: css`
    fill: ${theme.palette.insulin.basal.main};
    stroke: transparent;
  `,
  maxBasalLine: css`
    stroke: ${theme.palette.insulin.basal.main};
    stroke-width: 0.5;
  `,
});
